import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { hasPlanFeature, getPlanDefinition, getPlanLimit } from '../utils/planAccess';
import UpgradeModal from './UpgradeModal';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useQrOrders } from '../context/QrOrdersContext';
import { canFiscalRelay, fiscalForward } from '../utils/printService';
import { offlineEnabled } from '../utils/offlineCache';
import { isOffline as netIsOffline } from '../utils/netStatus';
import FiskalKassaModal from './FiskalKassaModal';

export default function AdminSidebar({ open = false, onClose = () => {} }) {
    const navigate  = useNavigate();
    const location  = useLocation();

    // Close sidebar on route change (mobile)
    useEffect(() => { onClose(); }, [location.pathname, location.search]); // eslint-disable-line
    const { user, logout } = useAuthStore();
    const { qrCount, openModal: openQrModal } = useQrOrders() || {};

    const plan          = getPlanDefinition(user?.tenant_plan);
    const isSuperAdmin  = user?.role === 'superadmin';

    // Offline rejim izləmə — etibarlı net-status (C# navigator.onLine yanlışdır) + 5 san poll
    const [netOffline, setNetOffline] = useState(netIsOffline());
    useEffect(() => {
        const sync = () => setNetOffline(netIsOffline());
        window.addEventListener('online', sync);
        window.addEventListener('offline', sync);
        window.addEventListener('net-status', sync); // netMonitor keçidi — dərhal
        const iv = setInterval(sync, 5000);
        return () => { window.removeEventListener('online', sync); window.removeEventListener('offline', sync); window.removeEventListener('net-status', sync); clearInterval(iv); };
    }, []);
    const offlineMode = offlineEnabled() && netOffline; // flag açıq + şəbəkə yox
    // Offline bağlanmış, fiskal çeki kəsilməmiş hesablar (yalnız offline tikli tenantda, online ikən)
    const [fiscalPendingCount, setFiscalPendingCount] = useState(0);
    useEffect(() => {
        if (!offlineEnabled()) return undefined;
        let alive = true;
        const loadFp = () => {
            if (netIsOffline()) return;
            api.get('/orders/fiscal-pending').then((r) => { if (alive) setFiscalPendingCount((r.data?.data || []).length); }).catch(() => {});
        };
        loadFp();
        window.addEventListener('app-resync', loadFp);
        return () => { alive = false; window.removeEventListener('app-resync', loadFp); };
    }, []);


    // "Ev vitrini" yalnız superadmin tick qoyubsa görünür (qr_cover_booking_enabled).
    // Tenant ayarıdır, JWT-də yoxdur → bir dəfə çəkilir və sessiyada keşlənir.
    const [bookingOn, setBookingOn] = useState(() => sessionStorage.getItem('house_booking_on') === '1');
    const [banquetOn, setBanquetOn] = useState(() => sessionStorage.getItem('banquet_on') === '1');
    useEffect(() => {
        if (!['admin', 'superadmin'].includes(user?.role)) return;
        let alive = true;
        api.get('/house-listing/settings')
            .then((r) => {
                if (!alive) return;
                const on  = Number(r.data?.data?.qr_cover_booking_enabled || 0) === 1;
                const bnq = Number(r.data?.data?.qr_cover_banquet_enabled || 0) === 1;
                setBookingOn(on); setBanquetOn(bnq);
                try {
                    sessionStorage.setItem('house_booking_on', on ? '1' : '0');
                    sessionStorage.setItem('banquet_on', bnq ? '1' : '0');
                } catch { /* ignore */ }
            })
            .catch(() => { /* icazə/şəbəkə xətası → gizli qalsın */ });
        return () => { alive = false; };
    }, [user?.role]);

    // Feature flags
    const hasRental      = hasPlanFeature(user?.tenant_plan, 'rental');
    const hasPurchases   = hasPlanFeature(user?.tenant_plan, 'purchases');
    const hasExpenses    = hasPlanFeature(user?.tenant_plan, 'expenses');
    const hasStaff       = hasPlanFeature(user?.tenant_plan, 'staff');
    const hasWarehouse   = hasPlanFeature(user?.tenant_plan, 'warehouse');
    const hasReports     = hasPlanFeature(user?.tenant_plan, 'reports');
    const hasBranding    = hasPlanFeature(user?.tenant_plan, 'branding');
    const hasRoom        = hasPlanFeature(user?.tenant_plan, 'room_management');
    const hasKassaLedger = hasPlanFeature(user?.tenant_plan, 'kassa_ledger');
    const hasSatis       = hasPlanFeature(user?.tenant_plan, 'satis');
    const hasOfis        = hasPlanFeature(user?.tenant_plan, 'ofis');
    const hasCashReg     = hasPlanFeature(user?.tenant_plan, 'cash_register');

    // ── Sidebar scroll persistence ────────────────────────────────────────────
    const menuScrollRef = useRef(null);
    // Restore scroll position on EVERY route change (not just mount)
    useEffect(() => {
        const saved = sessionStorage.getItem('admin_sidebar_scroll');
        if (saved && menuScrollRef.current) {
            // requestAnimationFrame ensures DOM is painted before scroll
            requestAnimationFrame(() => {
                if (menuScrollRef.current) menuScrollRef.current.scrollTop = Number(saved);
            });
        }
    }, [location.pathname]);
    const onMenuScroll = () => {
        if (menuScrollRef.current) sessionStorage.setItem('admin_sidebar_scroll', menuScrollRef.current.scrollTop);
    };

    // ── Fiskal kassa state ────────────────────────────────────────────────────
    const [upgradeModal,         setUpgradeModal]         = useState(false);
    const [cashboxStatus,        setCashboxStatus]        = useState(null);
    const [cashboxModal,         setCashboxModal]         = useState(false);
    const [cashboxLoading,       setCashboxLoading]       = useState(false);
    const [cashboxActionLoading, setCashboxActionLoading] = useState('');


    const loadCashboxStatus = useCallback(async () => {
        try {
            const res = await api.get('/cashbox/status');
            setCashboxStatus(res.data?.data || null);
        } catch { setCashboxStatus(null); }
    }, []);

    const openCashboxModal = async () => {
        setCashboxModal(true);
        setCashboxLoading(true);
        await loadCashboxStatus();
        setCashboxLoading(false);
    };

    const runCashboxAction = async (endpoint, successMessage) => {
        setCashboxActionLoading(endpoint);
        try {
            if (canFiscalRelay()) {
                // Kassa cihazı lokal şəbəkədə (private IP) → C# üzərindən relay
                const prep = await api.post('/cashbox/prepare', { action: endpoint });
                const fwd = await fiscalForward(prep.data.relay);
                const done = await api.post('/cashbox/complete', { action: endpoint, raw: fwd.raw, status: fwd.status });
                toast.success(done.data?.msg || successMessage);
            } else {
                await api.post(`/cashbox/${endpoint}`);
                toast.success(successMessage);
            }
            await loadCashboxStatus();
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Xəta baş verdi.');
        } finally { setCashboxActionLoading(''); }
    };


    // ── helpers ───────────────────────────────────────────────────────────────
    const isActive = (path) => {
        if (!path) return false;
        const [pathname, qs] = path.split('?');
        if (location.pathname !== pathname) return false;
        if (!qs) return true;
        const need    = new URLSearchParams(qs);
        const current = new URLSearchParams(location.search);
        for (const [k, v] of need) { if (current.get(k) !== v) return false; }
        return true;
    };

    const handleLockedClick = () => setUpgradeModal(true);

    // ── Btn component ─────────────────────────────────────────────────────────
    const Btn = ({ label, icon, path = '', color = 'default', onClick, locked, badge, lockedFor }) => {
        const isLockedByPlan = lockedFor?.includes(plan?.code) || locked;
        const active = isActive(path);
        const base   = 'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-all w-full relative';

        const cls = isLockedByPlan
            ? `${base} bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed`
            : active
                ? `${base} bg-blue-600 text-white border-blue-600 shadow-sm`
                : color === 'green'
                    ? `${base} bg-green-600 text-white border-green-600 hover:bg-green-700`
                    : color === 'red'
                        ? `${base} bg-red-500 text-white border-red-500 hover:bg-red-600`
                        : color === 'orange'
                            ? `${base} bg-orange-500 text-white border-orange-500 hover:bg-orange-600`
                            : `${base} bg-white text-slate-700 border-slate-200 hover:bg-slate-50`;

        const handleClick = isLockedByPlan
            ? handleLockedClick
            : (onClick || (() => navigate(path)));

        return (
            <button onClick={handleClick} className={cls}>
                <span className="text-sm leading-none shrink-0">{icon}</span>
                <span className="truncate leading-tight">{label}</span>
                {isLockedByPlan && <span className="ml-auto text-[10px]">🔒</span>}
                {badge > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                        {badge}
                    </span>
                )}
            </button>
        );
    };

    const Sec = ({ label }) => (
        <p className="pt-3 pb-1 px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {label}
        </p>
    );

    const Divider = () => <div className="my-1 border-t border-slate-200" />;

    // ── Superadmin view ───────────────────────────────────────────────────────
    if (isSuperAdmin) {
        return (
            <>
                {open && <div className="fixed inset-0 z-10 bg-black/40 md:hidden" onClick={onClose} />}
                <aside className={`fixed left-0 top-0 z-20 flex h-full w-64 flex-col border-r border-slate-200 bg-gray-50 shadow-sm transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                    <SidebarHeader user={user} plan={plan} onClose={onClose} />
                    <div className="flex-1 overflow-y-auto px-2.5 py-2 space-y-1">
                        <Sec label="Super Admin" />
                        <Btn icon="📊" label="Dashboard"    path="/superadmin/dashboard" />
                        <Btn icon="🏢" label="Restoranlar"  path="/superadmin/tenants" />
                    </div>
                    <SidebarFooter logout={() => logout(navigate)} />
                </aside>
            </>
        );
    }

    // ── Offline view (admin/kassir) — yalnız offline işləyən bölmələr ───────────
    if (offlineMode) {
        return (
            <>
            {open && <div className="fixed inset-0 z-10 bg-black/40 md:hidden" onClick={onClose} />}
            <aside className={`fixed left-0 top-0 z-20 flex h-full w-64 flex-col border-r border-slate-200 bg-gray-50 shadow-sm transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                <SidebarHeader user={user} plan={plan} onClose={onClose} />
                <div className="flex-1 overflow-y-auto px-2.5 py-2 space-y-1">
                    <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] font-bold text-amber-700 leading-snug">
                        📴 Offline rejim — yalnız sifarişlər işləyir. İnternet qayıdanda bütün bölmələr geri gəlir.
                    </div>
                    <Sec label="Sifariş" />
                    <Btn icon="🍽️" label="Masalar / Sifarişlər" path="/waiter/tables" />
                </div>
                <SidebarFooter logout={() => logout(navigate)} />
            </aside>
            </>
        );
    }

    // ── Admin view ────────────────────────────────────────────────────────────
    return (
        <>
        {open && <div className="fixed inset-0 z-10 bg-black/40 md:hidden" onClick={onClose} />}
        <aside className={`fixed left-0 top-0 z-20 flex h-full w-64 flex-col border-r border-slate-200 bg-gray-50 shadow-sm transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
            <SidebarHeader user={user} plan={plan} onClose={onClose} />

            <div ref={menuScrollRef} onScroll={onMenuScroll} className="flex-1 overflow-y-auto px-2.5 py-2 space-y-1">

                {/* Sifariş */}
                <Sec label="Sifariş" />
                <Btn icon="🍽️" label="Masalar / Sifarişlər" path="/waiter/tables" />
                <Btn icon="📲" label="QR Sifarişlər" onClick={openQrModal} badge={qrCount} />

                <Divider />

                {/* Admin */}
                <Sec label="Admin" />
                <Btn icon="📊" label="Dashboard"      path="/admin/dashboard" />
                <Btn icon="🍴" label="Menyu"          path="/admin/menu" />
                {/* Masalar+Zallar: grid only when Zallar is accessible */}
                {hasRoom ? (
                    <div className="grid grid-cols-2 gap-1">
                        <Btn icon="🪑" label="Masalar" path="/admin/tables" />
                        <Btn icon="🏠" label="Zallar"  path="/admin/rooms" />
                    </div>
                ) : (
                    <Btn icon="🪑" label="Masalar" path="/admin/tables" />
                )}
                <Btn icon="👥" label="İstifadəçilər" path="/admin/users" />
                {/* Hesabatlar + Tənzimləmə: show in grid only when both unlocked */}
                {(hasReports && hasBranding) && (
                    <div className="grid grid-cols-2 gap-1">
                        <Btn icon="📋" label="Hesabatlar"  path="/admin/reports" />
                        <Btn icon="⚙️" label="Tənzimləmə" path="/admin/settings" />
                    </div>
                )}
                {(hasReports && !hasBranding) && <Btn icon="📋" label="Hesabatlar"  path="/admin/reports" />}
                {(!hasReports && hasBranding) && <Btn icon="⚙️" label="Tənzimləmə" path="/admin/settings" />}
                {hasWarehouse  && <Btn icon="📦" label="Anbar"        path="/warehouse/dashboard" />}
                {/* Locked items — always at bottom of Admin section */}
                {!hasRoom      && <Btn icon="🏠" label="Zallar"      path="/admin/rooms"         locked />}
                {!hasWarehouse && <Btn icon="📦" label="Anbar"        path="/warehouse/dashboard" locked />}
                {!hasReports   && <Btn icon="📋" label="Hesabatlar"  path="/admin/reports"        locked />}
                {!hasBranding  && <Btn icon="⚙️" label="Tənzimləmə" path="/admin/settings"       locked />}

                <Divider />

                {/* Kirayə */}
                <Sec label="Kirayə" />
                {hasRental ? (
                    <>
                        <div className="grid grid-cols-2 gap-1">
                            <Btn icon="🏠" label="Evlər"    path="/admin/rental?tab=evler" />
                            <Btn icon="🧑" label="Qonaqlar" path="/admin/rental?tab=qonaqlar" />
                        </div>
                        <Btn icon="🍳" label="Səhər yeməyi (avto)" path="/admin/breakfast" />
                        {bookingOn && <Btn icon="🏡" label="Ev vitrini" path="/admin/rental/listing" />}
                    </>
                ) : (
                    <div className="grid grid-cols-2 gap-1">
                        <Btn icon="🏠" label="Evlər"    locked />
                        <Btn icon="🧑" label="Qonaqlar" locked />
                    </div>
                )}
                {/* Banket — rental-dan müstəqil (menu_sets xüsusiyyəti), superadmin tick-i ilə */}
                {banquetOn && <Btn icon="🍽️" label="Banket menyu" path="/admin/banquet" />}

                <Divider />

                {/* Ödəniş */}
                <Sec label="Ödəniş" />
                <Btn icon="💱" label="Nağd / POS dəyişmə" path="/admin/payment-change" />
                <Btn icon="🗑️" label="Silinmiş hesablar" path="/admin/cancelled-orders" />
                <Btn icon="🖨️" label="Qaimə yenidən çap" path="/admin/reprint-qaime" />
                <Divider />

                {/* Bazarlıq */}
                <Sec label="Bazarlıq" />
                {hasPurchases ? (
                    <>
                        <div className="grid grid-cols-2 gap-1">
                            <Btn icon="🛒" label="Alışlar"  color="green" path="/admin/bazarliq?tab=alislar" />
                            <Btn icon="🧹" label="Digər"    color="red"   path="/admin/bazarliq?tab=diger" />
                        </div>
                        <Btn icon="🏢" label="Tədarükçülər" path="/admin/bazarliq?tab=tedarukculer" />
                    </>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-1">
                            <Btn icon="🛒" label="Alışlar" locked />
                            <Btn icon="🧹" label="Digər"   locked />
                        </div>
                        <Btn icon="🏢" label="Tədarükçülər" locked />
                    </>
                )}

                <Divider />

                {/* Maliyyə */}
                <Sec label="Maliyyə" />
                {hasKassaLedger
                    ? <Btn icon="💰" label="Kassa Hesabatı"   path="/admin/kassa-ledger" />
                    : <Btn icon="💰" label="Kassa Hesabatı"   locked />}
                {hasSatis
                    ? <Btn icon="📦" label="Satış"            path="/admin/satis" />
                    : <Btn icon="📦" label="Satış"            locked />}
                {hasOfis
                    ? <Btn icon="🏛️" label="Ofis / Ofis - 2" path="/admin/ofis" />
                    : <Btn icon="🏛️" label="Ofis / Ofis - 2" locked />}
                {hasOfis
                    ? <Btn icon="🏢" label="Kassa → Ofis"     path="/admin/kassa-ofis" />
                    : <Btn icon="🏢" label="Kassa → Ofis"     locked />}
                {hasCashReg
                    ? <Btn icon="💳" label="Fiskal Kassa"     onClick={openCashboxModal} />
                    : <Btn icon="💳" label="Fiskal Kassa"     locked />}
                {offlineEnabled() && (
                    <Btn icon="🧾" label="Fiskal gözləyənlər" path="/admin/fiscal-pending" badge={fiscalPendingCount || undefined} />
                )}
                {user?.role === 'admin' && !user?.is_manager && (
                    <Btn icon="🧾" label="Ödəniş və abunə" path="/admin/odenis" />
                )}
                {/* Locked at bottom */}
                {hasExpenses
                    ? <Btn icon="💸" label="Xərclər"          path="/admin/expenses" />
                    : <Btn icon="💸" label="Xərclər"          locked />}
                {hasStaff
                    ? <Btn icon="👥" label="İşçilər / Maaş"   path="/admin/staff" />
                    : <Btn icon="👥" label="İşçilər / Maaş"   locked />}
                <Btn icon="🔑" label="Ofisiantlar"       path="/admin/ofisiantlar" />

            </div>

            <SidebarFooter logout={() => logout(navigate)} />
        </aside>

        {/* ── Upgrade Modal ────────────────────────────────────────────────── */}
        <UpgradeModal
            open={upgradeModal}
            onClose={() => setUpgradeModal(false)}
            title={`${plan?.label || 'Pulsuz'} paket istifadə edirsiniz`}
            message="Bu funksiyadan istifadə etmək üçün planınızı yüksəldin."
            plans={['basic', 'pro']}
            currentPlan={user?.tenant_plan}
        />

        {/* ── Fiskal Kassa Modal (ortaq komponent: Caspos+SmartOne+TapXPhone) ── */}
        <FiskalKassaModal open={cashboxModal} onClose={() => setCashboxModal(false)} />
        </>
    );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SidebarHeader({ user, plan, onClose }) {
    return (
        <div className="border-b border-slate-200 bg-white px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-center mb-2 relative">
                <img src="/FiscalPOS_blue.png" alt="FiscalPOS" className="h-8 w-auto object-contain" />
                {/* Close button — mobile only */}
                <button
                    onClick={onClose}
                    className="absolute right-0 md:hidden flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-slate-500 text-base hover:bg-slate-200 transition-all"
                    aria-label="Bağla"
                >
                    ✕
                </button>
            </div>
            <div className="flex items-center gap-2 min-w-0">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-xs">
                    {user?.name?.[0] || '?'}
                </div>
                <div className="min-w-0">
                    <p className="truncate font-bold text-slate-800 text-xs">{user?.name}</p>
                    <p className="truncate text-[10px] text-slate-400">{user?.tenant_name}</p>
                </div>
                {plan && (
                    <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${plan.badgeClass}`}>
                        {plan.label}
                    </span>
                )}
            </div>
        </div>
    );
}

function SidebarFooter({ logout }) {
    const { isImpersonating, exitImpersonation } = useAuthStore();
    const navigate = useNavigate();
    const impersonating = isImpersonating && isImpersonating();
    return (
        <div className="border-t border-slate-200 p-2.5 flex-shrink-0">
            {impersonating ? (
                // Superadminden yönləndirilibsə → "Çıxış" əvəzinə geri qayıtma
                <button
                    onClick={() => { exitImpersonation(); navigate('/superadmin/impersonate'); }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-purple-200 bg-purple-50 py-2 text-xs font-bold text-purple-700 hover:bg-purple-600 hover:text-white transition-all"
                >
                    ← Superadmin-ə qayıt
                </button>
            ) : (
                <button
                    onClick={logout}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 py-2 text-xs font-bold text-red-500 hover:bg-red-500 hover:text-white transition-all"
                >
                    🚪 Çıxış
                </button>
            )}
        </div>
    );
}
